import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { loadTestData } from './ConversationTestActions';
import useNeuroState, {useDispatch} from '../../../state';

const localViewOptionsKey = 'conversationTestPageOptions';
/**
 * @typedef ConversationTestContextObject
 * @property {function(view:Object)} changeView - change the current view
 * @property {function(view:Object)} changeViewOptions - change the view options
 * @property {Object} selectedOptions - the selected view options toggles
 * @property {Object} selectedView - current view being used on the conversation test
 * @property {string} uuid - the uuid of the current user
 * @property {Object} views - the available views
 * @type ConversationTestContextObject
 */
const defaultValue = {};
const ConversationTestContext = createContext(defaultValue);

const ConversationTestProvider = ({ children, defaultView, views }) => {
  const { user } = useNeuroState();
  const dispatch = useDispatch();
  const [activeView, setActiveView] = useState(defaultView);
  const [selectedOptions, setSelectedOptions] = useState(
    JSON.parse(window.sessionStorage.getItem(localViewOptionsKey)) ||
      activeView?.options ||
      {}
  );

  const changeView = (updatedView, options = {}) => {
    if (updatedView?.name && views?.[updatedView.name]) {
      const view = views?.[updatedView.name];
      const relativeOptions = {
        ...selectedOptions,
        ...options,
      };
      Object.keys(relativeOptions).map((o) => {
        /* eslint-disable  no-prototype-builtins */
        if (!view.options.hasOwnProperty(o)) delete relativeOptions[o];
      });
      setActiveView({
        ...view,
        options: {
          ...view.options,
          ...relativeOptions,
        },
      });
    }
  };

  const changeViewOptions = (options) => {
    const updatedOptions = {
      ...selectedOptions,
      ...options,
    };
    window.sessionStorage.setItem(
      localViewOptionsKey,
      JSON.stringify(updatedOptions)
    );
    setSelectedOptions(updatedOptions);
    changeView(activeView, updatedOptions);
  };

  useEffect(() => {
    dispatch(loadTestData());
    changeView(defaultView);
  }, []);

  /**
   * @type ConversationTestContextObject
   */
  const contextValues = {
    changeView,
    changeViewOptions,
    selectedOptions: activeView?.options || {},
    selectedView: activeView,
    uuid: user?.uuid,
    views,
  };
  return (
    <ConversationTestContext.Provider value={contextValues}>
      {children}
    </ConversationTestContext.Provider>
  );
};

/**
 * @returns ConversationTestContextObject
 */
function useConversationTest() {
  return useContext(ConversationTestContext);
}

ConversationTestProvider.defaultProps = {
  children: null,
  defaultView: null,
  views: {},
};
ConversationTestProvider.propTypes = {
  children: PropTypes.node,
  defaultView: PropTypes.shape({}).isRequired,
  views: {},
};
export { useConversationTest, ConversationTestProvider };
